import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Buildings, CaretDown, Gear, List, Question } from '@phosphor-icons/react';
import ClientSelector from './ClientSelector';
import PhoneMenu from './PhoneMenu';
import Menu from '../ui/Menu';
import { useHelp } from '../../contexts/HelpContext';
import { useClientSelection } from '../../contexts/ClientSelectionContext';
import {
  getVisibleNav,
  isTabActive,
  matchGroupId,
  matchSection,
  rememberSection,
  resolveGroupPath,
  type ModuleId,
} from '../../lib/nav';
import { shellContainer } from './shell';

const REPORT_BUG_URL = 'https://recorder.jam.dev/58KVX2B';

interface HeaderProps {
  isAdmin?: boolean;
  enabledModules?: ModuleId[];
  user?: { email?: string; companyName?: string; contactName?: string };
  onLogout: () => void;
}

/** Compact acting-client chip for the phone header; opens the phone menu. */
function PhoneClientChip({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();
  const { showSelector, effectiveClientId, clients } = useClientSelection();
  if (!showSelector) return null;
  const name = clients.find((c) => c.id === effectiveClientId)?.name ?? t('nav.selectClientPlaceholder');
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${t('nav.client')}: ${name}`}
      data-testid="nav-phone-client"
      className="flex h-9 min-w-[92px] max-w-[132px] shrink items-center gap-1.5 rounded-lg border border-white/20 bg-white/10 px-2.5 text-[13px] text-white"
    >
      <Buildings className="h-[15px] w-[15px] flex-none text-navy-300" weight="regular" aria-hidden="true" />
      <span className="max-w-[10ch] truncate whitespace-nowrap" data-testid="nav-phone-client-name">{name}</span>
    </button>
  );
}

export default function Header({
  isAdmin = false,
  enabledModules = ['shipping', 'rma', 'claims', 'replenishment'],
  user,
  onLogout,
}: HeaderProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { openHelp } = useHelp();
  const [phoneOpen, setPhoneOpen] = useState(false);

  const nav = useMemo(() => getVisibleNav(enabledModules, isAdmin), [enabledModules, isAdmin]);
  const activeSection = matchSection(location.pathname);
  const activeGroupId = matchGroupId(location.pathname);
  // Section row: the active group's sections (Dashboard and Admin have none to show).
  const activeGroup = nav.groups.find((g) => g.id === activeGroupId);
  const sectionRow = activeGroup && activeGroup.sections.length > 0 ? activeGroup.sections : null;
  const { showSelector } = useClientSelection();
  // Unmatched paths (profile, unknown) get the neutral "Menu", never a wrong group.
  const activeLabelKey =
    activeGroupId === 'admin' ? 'nav.admin' : nav.groups.find((g) => g.id === activeGroupId)?.labelKey;
  const phoneGroupLabel = t(activeLabelKey ?? 'nav.menu');

  const toggleLanguage = () => {
    void i18n.changeLanguage(i18n.language === 'en' ? 'fr' : 'en');
  };
  const reportBug = () => {
    window.open(REPORT_BUG_URL, '_blank', 'noopener');
  };

  // Remember the last section visited in each group.
  useEffect(() => {
    if (activeSection) rememberSection(activeSection.groupId, activeSection.id);
  }, [activeSection]);

  // Close the phone menu on navigation.
  useEffect(() => {
    setPhoneOpen(false);
  }, [location.pathname]);

  // Keyboard shortcuts: Ctrl/Cmd+1-N jumps between groups.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key >= '1' && e.key <= '9') {
        const group = nav.groups[parseInt(e.key, 10) - 1];
        if (group) {
          e.preventDefault();
          navigate(resolveGroupPath(group));
        }
      }
    },
    [nav, navigate],
  );
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const closePhone = useCallback(() => setPhoneOpen(false), []);

  // Top row (board "Top menu option A"): text only, active = white + light-blue underline.
  const topItem =
    'flex h-[60px] items-center px-3.5 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-300';
  const topIdle = 'font-medium text-navy-200 hover:text-white';
  const topActive = 'bg-white/[0.07] font-semibold text-white shadow-[inset_0_-3px_0_#60b8f9]';

  return (
    <>
      <header className="sticky top-0 z-header">
        {/* ─────────── TOP ROW ─────────── */}
        <div className="primary-nav">
          <div className={shellContainer(location.pathname)}>
            {/* Desktop */}
            <div className="hidden h-[60px] items-center gap-8 lg:flex">
              <NavLink to="/dashboard" className="flex-shrink-0" aria-label="Arivo">
                <img src="/logo.png?v=2" alt="Arivo" className="h-11 w-auto" />
              </NavLink>

              <nav aria-label={t('nav.groups')} className="flex h-full min-w-0 items-stretch gap-1">
                {nav.groups.map((group, idx) => {
                  const active = activeGroupId === group.id;
                  return (
                    <Link
                      key={group.id}
                      to={resolveGroupPath(group)}
                      data-testid={`nav-group-${group.id}`}
                      aria-current={active ? 'page' : undefined}
                      title={`${t(group.labelKey)} (Ctrl+${idx + 1})`}
                      className={clsx(topItem, active ? topActive : topIdle)}
                    >
                      {t(group.labelKey)}
                    </Link>
                  );
                })}
              </nav>

              <div className="ml-auto flex flex-shrink-0 items-center gap-1.5">
                {nav.admin && (
                  <NavLink
                    to="/admin/users"
                    data-testid="nav-admin"
                    className={clsx(
                      'flex h-10 items-center gap-2 rounded-lg px-3 text-sm font-medium transition-colors',
                      activeGroupId === 'admin' ? 'bg-white/[0.07] text-white' : 'text-navy-200 hover:text-white',
                    )}
                  >
                    <Gear className="h-[18px] w-[18px]" weight="regular" aria-hidden="true" />
                    {t('nav.admin')}
                  </NavLink>
                )}
                <button
                  type="button"
                  onClick={() => openHelp()}
                  data-testid="nav-help"
                  aria-label={t('nav.help')}
                  title={t('nav.help')}
                  className="flex h-10 w-10 items-center justify-center rounded-lg text-navy-200 hover:text-white"
                >
                  <Question className="h-5 w-5" weight="regular" />
                </button>
                <div data-testid="nav-client-selector" className="ml-2">
                  <ClientSelector className="w-[210px]" />
                </div>
                <div data-testid="nav-account" className="ml-1.5">
                  <Menu
                    align="right"
                    hideCaret
                    ariaLabel={t('nav.account')}
                    buttonClassName="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-300"
                    label={(user?.contactName || user?.companyName || user?.email || 'U').charAt(0).toUpperCase()}
                    items={[
                      {
                        key: 'language',
                        label: i18n.language === 'en' ? 'Français' : 'English',
                        onSelect: toggleLanguage,
                      },
                      { key: 'profile', label: t('nav.profile'), onSelect: () => navigate('/profile') },
                      { key: 'bug', label: t('nav.reportBug'), onSelect: reportBug },
                      { key: 'logout', label: t('nav.logout'), onSelect: onLogout, danger: true },
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Phone: logo, current group, client chip, menu */}
            <div className="flex h-14 items-center gap-2.5 lg:hidden">
              <NavLink to="/dashboard" className="flex-shrink-0" aria-label="Arivo">
                <img src="/logo.png?v=2" alt="Arivo" className="h-8 w-8 object-cover object-left" />
              </NavLink>
              <button
                type="button"
                onClick={() => setPhoneOpen(true)}
                data-testid="nav-phone-group"
                aria-label={activeLabelKey ? `${t('nav.menu')}: ${phoneGroupLabel}` : t('nav.menu')}
                className="inline-flex h-10 min-w-0 shrink items-center gap-1.5 px-1 text-[15px] font-semibold text-white"
              >
                <span className="truncate">{phoneGroupLabel}</span>
                <CaretDown className="h-3.5 w-3.5 text-navy-300" weight="bold" aria-hidden="true" />
              </button>
              <span className="min-w-0 flex-1" />
              <PhoneClientChip onOpen={() => setPhoneOpen(true)} />
              <button
                type="button"
                onClick={() => setPhoneOpen(true)}
                aria-label={t('nav.menu')}
                aria-haspopup="dialog"
                aria-expanded={phoneOpen}
                data-testid="nav-phone-toggle"
                className="-mr-2 flex h-11 w-11 flex-none shrink-0 items-center justify-center text-white hover:bg-white/10"
              >
                <List className="h-[22px] w-[22px]" weight="regular" />
              </button>
            </div>
          </div>
        </div>

        {/* ─────────── SECTION ROW (plain text, same left edge as the page) ─────────── */}
        {sectionRow && (
          <div className="relative border-b border-slate-200 bg-white">
            <div className={shellContainer(location.pathname)}>
              <nav
                aria-label={t('nav.sections')}
                className="flex h-12 items-stretch gap-[22px] overflow-x-auto whitespace-nowrap text-sm lg:gap-7 lg:overflow-visible"
                data-testid="nav-sections"
              >
                {sectionRow.map((s) => {
                  const active = activeSection?.id === s.id;
                  return (
                    <div key={s.id} className="flex flex-none items-stretch">
                      <Link
                        to={s.path}
                        data-testid={`nav-section-${s.id}`}
                        aria-current={active ? 'page' : undefined}
                        className={clsx(
                          'flex items-center transition-colors',
                          active
                            ? 'font-semibold text-navy-800 shadow-[inset_0_-2px_0_#1b3a5c]'
                            : 'font-medium text-slate-600 hover:text-slate-900',
                        )}
                      >
                        {t(s.labelKey)}
                      </Link>
                      {s.quickJump && s.tabs.length > 0 && (
                        <div className="hidden items-center lg:flex">
                          <Menu
                            hideCaret
                            ariaLabel={t('nav.quickJump', { section: t(s.labelKey) })}
                            buttonClassName="ml-1 flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                            label={<CaretDown className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />}
                            items={s.tabs.map((tab) => ({
                              key: tab.path,
                              label: t(tab.labelKey),
                              current: active && isTabActive(tab, location.pathname),
                              onSelect: () => navigate(tab.path),
                            }))}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute bottom-px right-0 top-0 w-12 bg-gradient-to-r from-white/0 to-white lg:hidden"
            />
          </div>
        )}
      </header>

      <PhoneMenu
        open={phoneOpen}
        onClose={closePhone}
        nav={nav}
        user={user}
        clientSelector={showSelector ? <ClientSelector className="w-full" /> : undefined}
        onLogout={onLogout}
        onToggleLanguage={toggleLanguage}
        onOpenHelp={() => {
          setPhoneOpen(false);
          openHelp();
        }}
        onReportBug={() => {
          setPhoneOpen(false);
          reportBug();
        }}
      />

      <style>{`
        .primary-nav {
          background: linear-gradient(180deg, #263f63 0%, #1e3555 100%);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </>
  );
}
